import DestinationTypes from '../const/DestinationTypes';
import _ from 'lodash';
import * as CardDestinationUtils from './CardDestinationUtils';

export function getEmpty() {
  return {
    campaigns: [],
    presetProvider: null,
    visitedCampaigns: []
  };
}

export function getEmptyCampaign(id = null) {
  return {
    id,
    networks: []
  };
}

export function getCampaignFromPreference(preference, campaignId) {
  return _.find(preference.campaigns, {id: campaignId});
}

export function setDestination(preference, campaignId, destinationName, destinationKind) {
  if (!preference) {
    preference = getEmpty();
  }

  preference.campaigns = preference.campaigns || [];
  const existing = _.find(preference.campaigns, {id: campaignId});
  if (existing) {
    preference.campaigns = [..._.without(preference.campaigns, existing), existing];
  } else {
    preference.campaigns.push(getEmptyCampaign(campaignId));
  }

  if (preference.campaigns.length > 10) {
    preference.campaigns = preference.campaigns.slice(preference.campaigns.length - 10);
  }

  const latestCampaign = _.last(preference.campaigns);

  setDestinationInternal(latestCampaign, destinationName, destinationKind);
  return preference;
}

export function setPresetProvider(preference, providerName) {
  if (!preference) {
    preference = getEmpty();
  }

  preference.presetProvider = providerName;
  return preference;
}

function setDestinationInternal(campaign, destinationName, destinationKind) {
  switch (destinationKind) {
    case DestinationTypes.provider:
      campaign.provider = destinationName;
      break;
    case DestinationTypes.network:
      if (campaign.networks.indexOf(destinationName) < 0) {
        campaign.networks.push(destinationName);
      }
      break;
    case DestinationTypes.customDestination:
      campaign.customDestination = destinationName;
      break;
    default:
      break;
  }
  return campaign;
}

export function removeDestination(preference, campaignId, destinationName, destinationKind) {
  if (!preference) {
    return null;
  }

  const campaign = getCampaignFromPreference(preference, campaignId);
  if (campaign) {
    switch (destinationKind) {
      case DestinationTypes.provider:
        campaign.provider = null;
        break;
      case DestinationTypes.network: {
        const index = campaign.networks.indexOf(destinationName);
        if (index >= 0) {
          campaign.networks.splice(index, 1);
        }
        break;
      }
      case DestinationTypes.customDestination:
        campaign.customDestination = null;
        break;
      default:
        break;
    }

    if (!campaign.provider && !campaign.customDestination && _.isEmpty(campaign.networks)) {
      _.remove(preference.campaigns, {id: campaignId});
    }
  }

  preference = removeDestinationFromDeprecatedFormat(preference, destinationName, destinationKind);
  return preference;
}

function removeDestinationFromDeprecatedFormat(preference, destinationName, destinationKind) {
  switch (destinationKind) {
    case DestinationTypes.provider:
      delete preference.provider;
      break;
    case DestinationTypes.network: {
      if (preference.networks) {
        const index = preference.networks.indexOf(destinationName);
        if (index >= 0) {
          preference.networks.splice(index, 1);
        }
      }
      if (_.isEmpty(preference.networks)) {
        delete preference.networks;
      }
      break;
    }
    case DestinationTypes.customDestination:
      delete preference.customDestination;
      break;
    default:
      break;
  }
  return preference;
}

export function addVisitedCampaign(preference, campaignId) {
  if (preference) {
    preference.visitedCampaigns = preference.visitedCampaigns || [];
    if (preference.visitedCampaigns.indexOf(campaignId) < 0) {
      preference.visitedCampaigns.push(campaignId);
    }

    if (preference.visitedCampaigns.length > 100) {
      preference.visitedCampaigns = preference.visitedCampaigns.slice(preference.visitedCampaigns.length - 100);
    }
  }
  return preference;
}

export function hasVisitedCampaign(preference, campaignId) {
  return !!(_.get(preference, 'visitedCampaigns', []).indexOf(campaignId) >= 0);
}

export function getDestinationForCard(preference, card) {
  if (preference) {
    const validDestinations = _(card.destinations)
      .filter(d => !!d.url && d.isActive)
      .uniqBy(d => d.name)
      .value();

    let destination,
      kind;
    if (!_.isEmpty(preference.campaigns)) {
      for (let i = preference.campaigns.length - 1; i >= 0; i--) { // latest visited campaigns at the end
        const campaign = preference.campaigns[i];
        kind = null;
        destination = _.find(validDestinations, {name: campaign.provider});
        if (destination) {
          kind = DestinationTypes.provider;
          break;
        }

        const networkDestination = _.find(validDestinations, {kind: 'network'});
        if (networkDestination && _.includes(campaign.networks, networkDestination.name)) {
          destination = networkDestination;
          kind = DestinationTypes.network;
          break;
        }

        destination = _.find(validDestinations, {name: campaign.customDestination});
        if (destination) {
          kind = DestinationTypes.customDestination;
          break;
        }
      }
    }

    if (!destination) {
      const deprecatedResult = getDestinationFromDeprecatedFormat(preference, validDestinations);
      if (deprecatedResult && deprecatedResult.destination) {
        destination = deprecatedResult.destination;
        kind = deprecatedResult.kind;
      }
    }

    if (!destination) {
      destination = _.find(validDestinations, {name: preference.presetProvider});
      if (destination) {
        kind = DestinationTypes.provider;
      }
    }

    if (destination) {
      return {
        transitionImageUrl: CardDestinationUtils.getTransitionImageUrl(destination),
        name: destination.name,
        url: destination.url,
        trackingCode: destination.trackingCode,
        kind
      };
    }
  }
  return null;
}

function getDestinationFromDeprecatedFormat(preference, destinations) {
  let kind = null;
  let destination = _.find(destinations, {name: preference.provider});
  if (destination) {
    kind = DestinationTypes.provider;
  }

  if (!destination) {
    const networkDestination = _.find(destinations, {kind: 'network'});
    if (networkDestination && _.includes(preference.networks, networkDestination.name)) {
      destination = networkDestination;
      kind = DestinationTypes.network;
    }
  }

  if (!destination) {
    destination = _.find(destinations, {name: preference.customDestination});
    if (destination) {
      kind = DestinationTypes.customDestination;
    }
  }

  if (destination) {
    return {
      destination,
      kind
    };
  }
  return null;
}
